import { useCallback, useEffect, useState } from "react";
import { Modal } from "../ui/Modal";
import { Spinner } from "../ui/Spinner";
import { newNotification } from "../ui/Notification";
import { api } from "../../api/api";
import { PaymentMethod } from "../../types";
import { newID } from "../utils/unique";
import { AutoForm } from "./AutoForm";
import { Icon } from "../ui/icons";
import { DataContext, DataMessages, useData, useDataContext, useDataEvents } from "../hooks/useData";
import { useConditional } from "../hooks/useConditional";
import { requiredRule } from "./validators";
import { useRefresh } from "../hooks/useRefresh";

declare const eWAY: any;


const cardTypeRegExprs = {
	//JCB
	jcb_regex: new RegExp('^(?:2131|1800|35)[0-9]{0,}$'), //2131, 1800, 35 (3528-3589)
	// American Express
	amex_regex: new RegExp('^3[47][0-9]{0,}$'), //34, 37
	// Diners Club
	diners_regex: new RegExp('^3(?:0[0-59]{1}|[689])[0-9]{0,}$'), //300-305, 309, 36, 38-39
	// Visa
	visa_regex: new RegExp('^4[0-9]{0,}$'), //4
	// MasterCard
	mastercard_regex: new RegExp('^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)[0-9]{0,}$'), //2221-2720, 51-55
	maestro_regex: new RegExp('^(5[06789]|6)[0-9]{0,}$'), //always growing in the range: 60-69, started with / not something else, but starting 5 must be encoded as mastercard anyway
	//Discover
	discover_regex: new RegExp('^(6011|65|64[4-9]|62212[6-9]|6221[3-9]|622[2-8]|6229[01]|62292[0-5])[0-9]{0,}$'),
	////6011, 622126-622925, 644-649, 65
}

export function CardIcon(props: {
	type: string,
}) {
	var icon = null;

	switch (props.type) {
		case "visa":
		case "mastercard":
			icon = <img style={{width:"32px",marginBottom:"4px"}} src={"/img/cc-" + props.type + ".jpg"} />;
			break;
	}

	if (!icon) return null; //icon = <Icon type="card"  />;

	return icon;
}

export function AutoUpdateCardIcon(props: {
	cardNumberProp: string,
}) {

	const [type, setType] = useState<any>();

	const data = useData();
	useDataEvents(data, useCallback((msg) => {
		if (msg.type === DataMessages.CHANGE && msg.propsChanged && msg.propsChanged.indexOf(props.cardNumberProp) >= 0) {
			const type = determineCardType(data.getValue(props.cardNumberProp));
			setType(type);
		}
	}, [data, props.cardNumberProp]));

	return <CardIcon type={type} />
}

export function determineCardType(cardNumber: string) {
	var type = "unknown";
	if (!cardNumber || cardNumber.length < 0) return type;

	var val = cardNumber;

	// get rid of anything but numbers
	val = val.replace(/\D/g, '');

	// checks per each, as their could be multiple hits
	//fix: ordering matter in detection, otherwise can give false results in rare cases
	if (val.match(cardTypeRegExprs.visa_regex)) {
		type = "visa";
	} else if (val.match(cardTypeRegExprs.mastercard_regex)) {
		type = "mastercard";
	} else if (val.match(cardTypeRegExprs.jcb_regex)) {
		type = "jcb";
	} else if (val.match(cardTypeRegExprs.amex_regex)) {
		type = "amex";
	} else if (val.match(cardTypeRegExprs.diners_regex)) {
		type = "diners_club";
		} else if (val.match(cardTypeRegExprs.discover_regex)) {
		type = "discover";
	} else if (val.match(cardTypeRegExprs.maestro_regex)) {
		if (val[0] == '5') { //started 5 must be mastercard
			type = "mastercard";
		} else {
			type = "maestro"; //maestro is all 60-69 which is not something else, thats why this condition in the end
		}
	}

	return type;
}


export function CardDetails(props: {
	pm: PaymentMethod,
	onEdit: () => void,
}) {

	const { pm } = props;

	const haveCard = pm && pm.cardNumber; // && pm.cardType;

	return (
		<div>
			<div><b>Payment method</b></div>

			{
				haveCard &&
				<div>
					<div className="flex horiz gap-3">
						<Icon type="card" />
						Card ending in <b>{pm.cardNumber.substring(pm.cardNumber.length-4)}</b> <CardIcon type={pm.cardType} />
						expirying <b>{`${pm.cardExpiryMonth}/${pm.cardExpiryYear}`}</b>
					</div>
				</div>
			}

			<button
				className="primary"
				onClick={props.onEdit}
			>{haveCard ? "Change" : "Add card details"}</button>

		</div>
	);
}

export function EditPaymentMethod(props: {

	//TODO: remove this
	onClose: () => void,

}) {

	const data = useData();

	const refreshPM = useRefresh();

	const [pm, setPM] = useState({} as PaymentMethod);
	useEffect(() => {
		api.payment_method().then((pm: PaymentMethod) => {
			setPM(pm);
		}).catch((e) => {

		});
	}, [refreshPM.token]);

	useEffect(() => {
		// when pm changes re-validate
		data.validate();
	}, [pm]);

	const editCard = useConditional(() => {
		return (
			<GatewayForm
				pm={pm}

				onSaved={() => {
					editCard.close();
					refreshPM.refresh();
				}}
				onClose={() => {
					editCard.close();
				}}
			/>
		);
	});

	useDataEvents(data, useCallback((msg) => {
		if (msg.type === DataMessages.VALIDATE) {
			if (
				pm 
				&& pm.givenName1 && pm.familyName 
				&& pm.cardNumber && pm.cardName && pm.cardExpiryMonth && pm.cardExpiryYear
			) {
				// got enough 
			} else {
				msg.context.errors.push({
					message: "Payment method required"
				});
			}

		}

	}, [pm]));


	if (!pm) {
		return null;
	}

	return (
		<div>
			<CardDetails pm={pm} onEdit={() => {
				editCard.show({});
			}} />

			{editCard.render()}
		</div>

	);
}


const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const years = [];
let y = (new Date()).getFullYear();
for(let i=0;i<10;i++) {
	years.push(y++);
}

const fields = [
	{
		prop: "givenName1", title: "First name",
		options: {
			size: 16,
		}
	},
	{
		prop: "familyName", title: "Last name",
		options: {
			size: 16,
		}
	},
	{
		prop: "companyName", title: "Company",
		options: {
			size: 30,
		}
	},
	{
		prop: "cardName", title: "Name of card holder",
		options: {
			autoComplete: "cc-name",
			size: 24
		}
	},
	{
		prop: "cardNumber", title: "Credit/debit card number",
		options: {
			autoComplete: "cc-number",
			size: 24
		}
	},
	{
		prop: "cardExpiryMonth", title: "Expiry month",
		options: {
			autoComplete: "cc-exp-month",
			controlType: "dropdown",
			items: [
				1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
			],
			getValue: (item: any) => item,
			getDisplay: (item: any) => item ? `${item} (${monthNames[item-1]})` : "",
		}
	},
	{
		prop: "cardExpiryYear", title: "Expiry year",
		options: {
			autoComplete: "cc-exp-year",
			controlType: "dropdown",
			items: years,
			getValue: (item: any) => item,
			getDisplay: (item: any) => item,
		}
	},
	{
		prop: "cardCVN", title: "CVN",
		options: {
			autoComplete: "cc-csc",
			size: 4
		}
	},
];


const validators = [
	{
		validate: (context: any) => {
			for(let f of fields) {
				if (f.prop !== "companyName") {
					requiredRule(context, f.prop);
				}
			}
		},
	}
]



async function ensure_eWay_script(src: string) {
	return new Promise((resolve, reject) => {
		// Check if script with this src already exists
		if (document.querySelector(`script[src="${src}"]`)) {
			resolve('Script already loaded');
			return;
		}

		const script = document.createElement('script');
		script.src = src;
		script.async = true;
		
		script.onload = () => resolve('Script loaded successfully');
		script.onerror = () => reject(new Error('Script load failed'));
		
		document.head.appendChild(script);
	});
}


export function GatewayForm(props: {

	pm: PaymentMethod,

	onSaved: () => void,
	onClose: () => void,

}) {

	const { pm } = props;

	const [id] = useState(newID());

	const [progressMessage, setProgressMessage] = useState<string | undefined>();

	const [task, setTask] = useState("");

	const [gatewayTX, setGatewayTX] = useState({
		accessCode: "",
	});

	const [ewayReady, setEwayReady] = useState(false);
	useEffect(() => {
		ensure_eWay_script("https://api.ewaypayments.com/JSONP/v3/js").then(() => {
			setEwayReady(true);

		}).catch((e) => {
			newNotification({
				message: "There was a problem trying to initialise the payment gateway, please try again later, or contact support if problem persists",
				style: "error",
			});

		})

	}, []);

	const data = useDataContext({
		initialValue: {
			givenName1: pm.givenName1,
			familyName: pm.familyName,
			companyName: pm.companyName,
		} as PaymentMethod,
		validators,
	});

	var progress = null;
	if (progressMessage) {
		progress = (
			<Modal
				title="Saving, please wait"
				size="width-30vw"
				showCancel={false}
			>
				<div className="center"><Spinner size="small" /> {progressMessage}</div>
			</Modal>
		);
	}

	function handleSubmit() {

		// call eWAY to process the request 
		eWAY.process(
			document.getElementById("gateway-form-" + id), 
			{ 
				autoRedirect: false, 
				onComplete: function (data: any) { 
					// this is a callback to hook into when the requests completes
					if (data.Is3DSecure) { 
						window.location.replace(data.RedirectUrl); 
					} 

					//e.g.:
					// {
					// 	AccessCode: "C3AB9AxWvZ0e0RXmU2P_0fdtX6IDr_6kObw_fxJ4M4u4SfEJ3Jk3vfFRsdA1Rq6M9H4IqswWLGz_QN8VkZx3ytAFvYVvsn87QgaOKTRP3E0TotiKIsFdooxqudQ6-6U0F18wglZG61LQ…",
					// 	Errors: null,
					// 	IsComplete: false,
					// 	RedirectUrl: "https://dev.castlebusinessgroup.com.au:3001/back_from_eway/?AccessCode=C3AB9AxWvZ0e0RXmU2P_0fdtX6IDr_6kObw_fxJ4M4u4SfEJ3Jk3vfFRsdA1Rq6M9H4Iq…"
					// }

					// were there any errors?
					if (data.errors) {
						newNotification({
							message: "There was a problem trying to communicate with the payment gateway, please try again later",
							style: "error",
						});

					} else {
						// success

						// send the gateway response to the server so it can finalise the tx
						api.payment_finalize_gateway_tx({
							AccessCode: data.AccessCode,
						}).then((response) => {

							props.onSaved(
								//data
							);

						}).catch((err) => {
							newNotification({
								message: "Failed attempting to finalise communications with the payment gateway",
								style: "error",
							});
							setProgressMessage(undefined);

						});
					}

				}, 
				onError: function (e: any) { 
					// this is a callback you can hook into when an error occurs 
					newNotification({
						message: "There was a problem trying to communicate with the payment gateway, please try again later",
						style: "error",
					});
					props.onClose();
				}, 
				onTimeout: function (e: any) { 
					// this is a callback you can hook into when the request times out
					newNotification({
						message: "A timeout has occurred while trying to communicate with the payment gateway, please try again later",
						style: "error",
					});
					props.onClose();
				} 
			}
		);
	}

	const map = {
		"cardName": "EWAY_CARDNAME",
		"cardNumber": "EWAY_CARDNUMBER",
		"cardExpiryMonth": "EWAY_CARDEXPIRYMONTH",
		"cardExpiryYear": "EWAY_CARDEXPIRYYEAR",
		"cardCVN": "EWAY_CARDCVN",
	} as any;

	let hiddenFields = [];
	for(let m in map) {
		const n = map[m];
		hiddenFields.push(
			<input type="hidden" name={n} value={(data.value as any)[m]} key={m} />
		);
	}

	function resetForm() {
		setTask("");
		setProgressMessage(undefined);
	}

	useEffect(() => {

		console.log("DEBUG: task: " + task);

		if (task === "get-accesscode") {
			api.payment_access_code_create({
				purpose: pm.token ? "update_token_customer" : "create_token_customer",
				redirectUrl: api.getPaymentGatewayReturnUrl(),
				paymentMethod: {
					givenName1: data.value.givenName1,
					familyName: data.value.familyName,
					country: "AU",
				}
			}).then((result: any) => {
				console.dir(result);

				if (result && result.accessCode) {
					//alert("send-form");

					setGatewayTX({
						accessCode: result.accessCode,
					});
					setTask("send-form");

				} else {
					newNotification({
						message: "A valid access code was not provided by the payment gateway",
						style: "error",
					});
					resetForm();
				}
			}).catch((e) => {
				newNotification({
					message: "Unable to get access code from the payment gateway",
					style: "error",
				});
				resetForm();
			});

		} else if (task === "send-form") {
			handleSubmit();

		}

	}, [task]);

	return (

		<DataContext.Provider value={data}>

			<Modal
				title="Card Details"
				onCancel={props.onClose}
				style={{width:"400px"}}

				onSave={() => {
					setTask("get-accesscode");
				}}
			>

				{!ewayReady && <Spinner size="large" />}

				{ewayReady &&
					<form
						id={"gateway-form-" + id}
						action={`https://secure-au.sandbox.ewaypayments.com/AccessCode/${gatewayTX.accessCode}`}
						method="POST"
						onSubmit={handleSubmit}
					>
						{/* <DataProvider values={dataValues}> */}
							
							<input type="hidden" name="EWAY_ACCESSCODE" value={gatewayTX.accessCode} />
							{hiddenFields}
							
							{/* {props.children} */}
							<AutoForm
								fields={fields}

								layout={(get) => {
									return (
										<div className="flex vert align-start">
											<h3>Bill to</h3>
											<div className="flex horiz gap-5 label-top">
												{get("givenName1")}
												{get("familyName")}
											</div>
											<div className="flex horiz gap-5 label-top margin-bottom-10">
												{get("companyName")}
											</div>
											<h3>Card details</h3>
											<div className="flex fill label-top">
												{get("cardName")}
											</div>
											<div className="flex horiz gap-5 label-top align-end">
												{get("cardNumber")}
												<AutoUpdateCardIcon cardNumberProp="cardNumber"  />
											</div>
											<div className="flex horiz gap-5 label-top">
												{get("cardExpiryMonth")}
												{get("cardExpiryYear")}
												{get("cardCVN")}
											</div>
										</div>
									);
								}}
							/>

						{/* </DataProvider> */}
						
						{progress}
					</form>
				}

			</Modal>

		</DataContext.Provider>
	);

}


export function parseEwayErrors(errs: string) {
	if (!errs || errs.length <= 0) return null;

	const defs: any = {
		"V6000": "Validation error",
		"V6001": "Invalid CustomerIP",
		"V6002": "Invalid DeviceID",
		"V6003": "Invalid Request PartnerID",
		"V6004": "Invalid Request Method",
		"V6010": "Invalid TransactionType, account not certified for eCome only MOTO or Recurring available",
		"V6011": "Invalid Payment TotalAmount",
		"V6012": "Invalid Payment InvoiceDescription",
		"V6013": "Invalid Payment InvoiceNumber",
		"V6014": "Invalid Payment InvoiceReference",
		"V6015": "Invalid Payment CurrencyCode",
		"V6016": "Payment Required",
		"V6017": "Payment CurrencyCode Required",
		"V6018": "Unknown Payment CurrencyCode",
		"V6019": "Cardholder identity authentication required",
		"V6020": "Cardholder Input Required",
		"V6021": "EWAY_CARDHOLDERNAME Required",
		"V6022": "EWAY_CARDNUMBER Required",
		"V6023": "EWAY_CARDCVN Required",
		"V6024": "Cardholder Identity Authentication One Time Password Not Active Yet",
		"V6025": "PIN Required",
		"V6033": "Invalid Expiry Date",
		"V6034": "Invalid Issue Number",
		"V6035": "Invalid Valid From Date",
		"V6039": "Invalid Network Token Status",
		"V6040": "Invalid TokenCustomerID",
		"V6041": "Customer Required",
		"V6042": "Customer FirstName Required",
		"V6043": "Customer LastName Required",
		"V6044": "Customer CountryCode Required",
		"V6045": "Customer Title Required",
		"V6046": "TokenCustomerID Required",
		"V6047": "RedirectURL Required",
		"V6048": "CheckoutURL Required when CheckoutPayment specified",
		"​V6049": "Invalid Checkout URL",
		"V6051": "Invalid Customer FirstName",
		"V6052": "Invalid Customer LastName",
		"V6053": "Invalid Customer CountryCode",
		"V6058": "Invalid Customer Title",
		"V6059": "Invalid RedirectURL",
		"V6060": "Invalid TokenCustomerID",
		"V6061": "Invalid Customer Reference",
		"V6062": "Invalid Customer CompanyName",
		"V6063": "Invalid Customer JobDescription",
		"V6064": "Invalid Customer Street1",
		"V6065": "Invalid Customer Street2",
		"V6066": "Invalid Customer City",
		"V6067": "Invalid Customer State",
		"V6068": "Invalid Customer PostalCode",
		"V6069": "Invalid Customer Email",
		"V6070": "Invalid Customer Phone",
		"V6071": "Invalid Customer Mobile",
		"V6072": "Invalid Customer Comments",
		"V6073": "Invalid Customer Fax",
		"V6074": "Invalid Customer URL",
		"V6075": "Invalid ShippingAddress FirstName",
		"V6076": "Invalid ShippingAddress LastName",
		"V6077": "Invalid ShippingAddress Street1",
		"V6078": "Invalid ShippingAddress Street2",
		"V6079": "Invalid ShippingAddress City",
		"V6080": "Invalid ShippingAddress State",
		"V6081": "Invalid ShippingAddress PostalCode",
		"V6082": "Invalid ShippingAddress Email",
		"V6083": "Invalid ShippingAddress Phone",
		"V6084": "Invalid ShippingAddress Country",
		"V6085": "Invalid ShippingAddress ShippingMethod",
		"V6086": "Invalid ShippingAddress Fax",
		"V6091": "Unknown Customer CountryCode",
		"V6092": "Unknown ShippingAddress CountryCode",
		"V6093": "Insufficient Address Information",
		"V6100": "Invalid EWAY_CARDNAME",
		"V6101": "Invalid EWAY_CARDEXPIRYMONTH",
		"V6102": "Invalid EWAY_CARDEXPIRYYEAR",
		"V6103": "Invalid EWAY_CARDSTARTMONTH",
		"V6104": "Invalid EWAY_CARDSTARTYEAR",
		"V6105": "Invalid EWAY_CARDISSUENUMBER",
		"V6106": "Invalid EWAY_CARDCVN",
		"V6107": "Invalid EWAY_ACCESSCODE",
		"V6108": "Invalid CustomerHostAddress",
		"V6109": "Invalid UserAgent",
		"V6110": "Invalid EWAY_CARDNUMBER",
		"V6111": "Unauthorised API Access, Account Not PCI Certified",
		"V6112": "Redundant card details other than expiry year and month",
		"V6113": "Invalid transaction for refund",
		"V6114": "Gateway validation error",
		"V6115": "Invalid DirectRefundRequest, Transaction ID",
		"V6116": "Invalid card data on original TransactionID",
		"V6117": "Invalid CreateAccessCodeSharedRequest, FooterText",
		"V6118": "Invalid CreateAccessCodeSharedRequest, HeaderText",
		"V6119": "Invalid CreateAccessCodeSharedRequest, Language",
		"V6120": "Invalid CreateAccessCodeSharedRequest, LogoUrl",
		"V6121": "Invalid TransactionSearch, Filter Match Type",
		"V6122": "Invalid TransactionSearch, Non numeric Transaction ID",
		"V6123": "Invalid TransactionSearch,no TransactionID or AccessCode specified",
		"V6124": "Invalid Line Items. The line items have been provided however the totals do not match the TotalAmount field",
		"V6125": "Selected Payment Type not enabled",
		"V6126": "Invalid encrypted card number, decryption failed",
		"V6127": "Invalid encrypted cvn, decryption failed",
		"V6128": "Invalid Method for Payment Type",
		"V6129": "Transaction has not been authorised for Capture/Cancellation",
		"V6130": "Generic customer information error",
		"V6131": "Generic shipping information error",
		"V6132": "Transaction has already been completed or voided, operation not permitted",
		"V6133": "Checkout not available for Payment Type",
		"V6134": "Invalid Auth Transaction ID for Capture/Void",
		"V6135": "PayPal Error Processing Refund",
		"V6136": "Original transaction does not exist or state is incorrect",
		"V6140": "Merchant account is suspended",
		"V6141": "Invalid PayPal account details or API signature",
		"V6142": "Authorise not available for Bank/Branch",
		"V6143": "Invalid Public Key",
		"V6144": "Method not available with Public API Key Authentication",
		"V6145": "Credit Card not allow if Token Customer ID is provided with Public API Key Authentication",
		"V6146": "Client Side Encryption Key Missing or Invalid",
		"V6147": "Unable to Create One Time Code for Secure Field",
		"V6148": "Secure Field has Expired",
		"V6149": "Invalid Secure Field One Time Code",
		"V6150": "Invalid Refund Amount",
		"V6151": "Refund amount greater than original transaction",
		"V6152": "Original transaction already refunded for total amount",
		"V6153": "Card type not support by merchant",
		"V6154": "Insufficent Funds Available For Refund",
		"V6155": "Missing one or more fields in request",
		"V6160": "Encryption Method Not Supported",
		"V6161": "Encryption failed, missing or invalid key",
		"V6165": "Invalid Click-to-Pay (Visa Checkout) data or decryption failed",
		"V6170": "Invalid TransactionSearch, Invoice Number is not unique",
		"V6171": "Invalid TransactionSearch, Invoice Number not found",
		"V6220": "Three domain secure XID invalid",
		"V6221": "Three domain secure ECI invalid",
		"V6222": "Three domain secure AVV invalid",
		"V6223": "Three domain secure XID is required",
		"V6224": "Three Domain Secure ECI is required",
		"V6225": "Three Domain Secure AVV is required",
		"V6226": "Three Domain Secure AuthStatus is required",
		"V6227": "Three Domain Secure AuthStatus invalid",
		"V6228": "Three domain secure Version is required",
		"V6230": "Three domain secure Directory Server Txn ID invalid",
		"V6231": "Three domain secure Directory Server Txn ID is required",
		"V6232": "Three domain secure Version is invalid",
		"V6501": "Invalid Amex InstallementPlan",
		"V6502": "Invalid Number Of Installements for Amex. Valid values are from 0 to 99 inclusive",
		"V6503": "Merchant Amex ID required",
		"V6504": "Invalid Merchant Amex ID",
		"V6505": "Merchant Terminal ID required",
		"V6506": "Merchant category code required",
		"V6507": "Invalid merchant category code",
		"V6508": "Amex 3D ECI required",
		"V6509": "Invalid Amex 3D ECI",
		"V6510": "Invalid Amex 3D verification value",
		"V6511": "Invalid merchant location data",
		"V6512": "Invalid merchant street address",
		"V6513": "Invalid merchant city",
		"V6514": "Invalid merchant country",
		"V6515": "Invalid merchant phone",
		"V6516": "Invalid merchant postcode",
		"V6517": "Amex connection error",
		"V6518": "Amex EC Card Details API returned invalid data",
		"V6520": "Invalid or missing Amex Point Of Sale Data",
		"V6521": "Invalid or missing Amex transaction date time",
		"V6522": "Invalid or missing Amex Original transaction date time",
		"V6530": "Credit Card Number in non Credit Card Field",

		"S5000": "System Error",
		"S5010": "Unknown error returned by gateway",
		"S5011": "PayPal Connection Error",
		"S5012": "PayPal Settings Error",
		"S5014": "Merchant setting Error",
		"S5016": "Apple Pay processing error",
		"S5020": "Transaction maximum time elapsed",
		"S5029": "API Rate Limit Exceeded",
		"S5085": "Started 3D Secure",
		"S5086": "Routed 3D Secure",
		"S5087": "Completed 3D Secure",
		"S5088": "PayPal Transaction Created",
		"S5099": "Incomplete (Access Code in progress/incomplete)",
		"S5666": "Transaction in unknown state",

		"F7000": "Undefined Fraud Error",
		"F7001": "	Challenged Fraud",
		"F7002": "Country Match Fraud",
		"F7003": "High Risk Country Fraud",
		"F7004": "Anonymous Proxy Fraud",
		"F7005": "Transparent Proxy Fraud",
		"F7006": "Free Email Fraud",
		"F7007": "International Transaction Fraud",
		"F7008": "Risk Score Fraud",
		"F7009": "	Denied Fraud",
		"F7010": "Denied by PayPal Fraud Rules",
		"F9001": "Custom Fraud Rule",
		"F9010": "High Risk Billing Country",
		"F9011": "High Risk Credit Card Country",
		"F9012": "High Risk Customer IP Address",
		"F9013": "High Risk Email Address",
		"F9014": "High Risk Shipping Country",
		"F9015": "Multiple card numbers for single email address",
		"F9016": "Multiple card numbers for single location",
		"F9017": "Multiple email addresses for single card number",
		"F9018": "Multiple email addresses for single location",
		"F9019": "Multiple locations for single card number",
		"F9020": "Multiple locations for single email address",
		"F9021": "Suspicious Customer First Name",
		"F9022": "Suspicious Customer Last Name",
		"F9023": "Transaction Declined",
		"F9024": "Multiple transactions for same address with known credit card",
		"F9025": "Multiple transactions for same address with new credit card",
		"F9026": "Multiple transactions for same email with new credit card",
		"F9027": "Multiple transactions for same email with known credit card",
		"F9028": "Multiple transactions for new credit card",
		"F9029": "Multiple transactions for known credit card",
		"F9030": "Multiple transactions for same email address",
		"F9031": "Multiple transactions for same credit card",
		"F9032": "Invalid Customer Last Name",
		"F9033": "Invalid Billing Street",
		"F9034": "Invalid Shipping Street",
		"F9037": "Suspicious Customer Email Address",
		"F9049": "Genuine Customer",
		"F9050": "High Risk Email Address and amount",
		"F9059": "No liability shift",
		"F9113": "Card issuing country differs from IP address country",

		"D4401": "Refer to Issuer",
		"D4402": "Refer to Issuer, special",
		"D4403": "No Merchant",
		"D4404": "Pick Up Card",
		"D4405": "Do Not Honour",
		"D4406": "Error",
		"D4407": "Pick Up Card, Special",
		"D4409": "Request In Progress",
		"D4412": "Invalid Transaction",
		"D4413": "Invalid Amount",
		"D4414": "Invalid Card Number",
		"D4415": "No Issuer",
		"D4417": "3D Secure Error",
		"D4419": "Re-enter Last Transaction",
		"D4421": "No Action Taken",
		"D4422": "Suspected Malfunction",
		"D4423": "Unacceptable Transaction Fee",
		"D4425": "Unable to Locate Record On File",
		"D4430": "Format Error",
		"D4431": "Bank Not Supported By Switch",
		"D4433": "Expired Card, Capture",
		"D4434": "Suspected Fraud, Retain Card",
		"D4435": "Card Acceptor, Contact Acquirer, Retain Card",
		"D4436": "Restricted Card, Retain Card",
		"D4437": "Contact Acquirer Security Department, Retain Card",
		"D4438": "PIN Tries Exceeded, Capture",
		"D4439": "No Credit Account",
		"D4440": "Function Not Supported",
		"D4441": "Lost Card",
		"D4442": "No Universal Account",
		"D4443": "Stolen Card",
		"D4444": "No Investment Account",
		"D4450": "Click-to-Pay (Visa Checkout) Transaction Error",
		"D4451": "Insufficient Funds",
		"D4452": "No Cheque Account",
		"D4453": "No Savings Account",
		"D4454": "Expired Card",
		"D4455": "Incorrect PIN",
		"D4456": "No Card Record",
		"D4457": "Function Not Permitted to Cardholder",
		"D4458": "Function Not Permitted to Terminal",
		"D4459": "Suspected Fraud",
		"D4460": "Acceptor Contact Acquirer",
		"D4461": "Exceeds Withdrawal Limit",
		"D4462": "Restricted Card",
		"D4463": "Security Violation",
		"D4464": "Original Amount Incorrect",
		"D4466": "Acceptor Contact Acquirer, Security",
		"D4467": "Capture Card",
		"D4475": "PIN Tries Exceeded",
		"D4476": "Invalidate Txn Reference",
		"D4481": "Accumulated Transaction Counter (Amount) Exceeded",
		"D4482": "CVV Validation Error",
		"D4483": "Acquirer Is Not Accepting Transactions From You At This Time",
		"D4484": "Acquirer Is Not Accepting This Transaction",
		"D4490": "Cut off In Progress",
		"D4491": "Card Issuer Unavailable",
		"D4492": "Unable To Route Transaction",
		"D4493": "Cannot Complete, Violation Of The Law",
		"D4494": "Duplicate Transaction",
		"D4495": "Amex Declined",
		"D4496": "System Error",
		"D4497": "MasterPass Error",
		"D4498": "PayPal Create Transaction Error",
		"D4499": "Invalid Transaction for Auth/Void",
	};

	var items = errs.split(",");
	var result = [];
	for(var i=0;i<items.length;i++) {
		const code = items[i].trim();
		const e = defs[code];
		if (e) {
			if (code.startsWith("F")) {
				// don't report exact fraud reason to them
				result.push("Unexpected problem");
			} else {
				result.push(e);
			}
		}
	}

	return result.join(", ");
}
